import { useEffect, useState } from "react";
import { Warning, X, Check } from "@phosphor-icons/react";

/**
 * DangerConfirm - two-step destructive confirmation modal.
 * Usage:
 *   const [askDelete, setAskDelete] = useState(null);
 *   <DangerConfirm
 *     open={!!askDelete}
 *     onClose={() => setAskDelete(null)}
 *     onConfirm={() => { doDelete(askDelete.id); setAskDelete(null); }}
 *     title="Eliminare il cliente?"
 *     subtitle={askDelete?.name}
 *     description="Questa azione è irreversibile. Verrà rimosso il cliente e tutti i suoi dati."
 *     confirmLabel="Elimina definitivamente"
 *     verifyWord="ELIMINA"
 *   />
 */
export default function DangerConfirm({
  open,
  onClose,
  onConfirm,
  title = "Confermare l'operazione?",
  subtitle,
  description,
  confirmLabel = "Conferma",
  verifyWord,
  testId,
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setTyped(""); setBusy(false); return; }
    const onKey = (e) => e.key === "Escape" && !busy && onClose?.();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, busy, onClose]);

  if (!open) return null;

  const wordRequired = verifyWord || null;
  const wordOk = !wordRequired || typed.trim().toUpperCase() === wordRequired.toUpperCase();

  const doConfirm = async () => {
    if (!wordOk || busy) return;
    setBusy(true);
    try {
      await onConfirm?.();
    } catch (e) { /* handled by caller */ }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(15,28,22,0.55)", backdropFilter: "blur(4px)" }}
      onClick={() => !busy && onClose?.()}
      data-testid={testId || "danger-confirm"}
    >
      <div
        className="bg-white w-full max-w-md flex flex-col overflow-hidden shadow-2xl"
        style={{ borderRadius: "20px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#FBDDE1] text-[var(--tf-error)] flex items-center justify-center flex-shrink-0">
            <Warning size={22} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-lg text-[var(--tf-ink)]">{title}</div>
            {subtitle && (
              <div className="text-sm text-[var(--tf-ink)] font-semibold mt-1 truncate">{subtitle}</div>
            )}
            {description && (
              <div className="text-xs text-[var(--tf-muted)] mt-2 leading-relaxed">{description}</div>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--tf-muted)] hover:bg-[var(--tf-bg)] transition flex-shrink-0"
            aria-label="Chiudi"
            data-testid="danger-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {wordRequired && (
          <div className="px-5 pb-2">
            <label className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold block mb-1.5">
              Per confermare, scrivi <b className="text-[var(--tf-error)] tracking-wider">{wordRequired}</b>
            </label>
            <input
              type="text"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className={`w-full rounded-2xl border-2 bg-white text-sm font-display font-bold tracking-wider text-[var(--tf-ink)] focus:outline-none transition ${
                typed && !wordOk
                  ? "border-[var(--tf-error)]"
                  : wordOk && typed
                  ? "border-[var(--tf-success)]"
                  : "border-[var(--tf-border)] focus:border-[var(--tf-ink)]"
              }`}
              style={{ padding: "12px 16px" }}
              placeholder={wordRequired}
              data-testid="danger-verify-input"
            />
          </div>
        )}

        <div className="p-5 flex gap-2 border-t border-[var(--tf-border)] mt-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 rounded-2xl border border-[var(--tf-border)] font-bold text-sm text-[var(--tf-ink)] hover:bg-[var(--tf-bg)] transition disabled:opacity-50"
            style={{ padding: "14px 16px" }}
            data-testid="danger-cancel"
          >
            Annulla
          </button>
          <button
            onClick={doConfirm}
            disabled={!wordOk || busy}
            className="flex-1 rounded-2xl bg-[var(--tf-error)] hover:bg-[#8A1024] disabled:bg-[var(--tf-border)] disabled:text-[var(--tf-muted)] disabled:cursor-not-allowed text-white font-bold text-sm flex items-center justify-center gap-2 transition"
            style={{ padding: "14px 16px" }}
            data-testid="danger-confirm-btn"
          >
            <Check size={14} weight="bold" /> {busy ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
